import { Outlet, useLocation } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { usePermissions } from '../../hooks/usePermissions'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import MainLayout from './MainLayout'
import MobileLayout from './MobileLayout'
import AdminAppLayout from './AdminAppLayout'
import VetOwnerLayout from './VetOwnerLayout'
import VetBasicLayout from './VetBasicLayout'

const PAGES_WITHOUT_SIDEBAR = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email']

export default function GlobalLayout() {
  const location = useLocation()
  const [forbiddenMessage, setForbiddenMessage] = useState(null)
  const { isDueno, isPetSitter, isSuperAdmin, isVeterinario, isVeterinarioAdmin } = usePermissions()
  const isMobileViewport = useMediaQuery('(max-width: 1023px)')
  const useMobileLayout = (isDueno || isPetSitter) && isMobileViewport
  const useAdminAppLayout = isSuperAdmin && !isMobileViewport
  const useVetOwnerLayout = isVeterinarioAdmin && !isSuperAdmin && !isMobileViewport
  const useVetBasicLayout = isVeterinario && !isVeterinarioAdmin && !isSuperAdmin && !isMobileViewport
  const forcedDarkRef = useRef(false)

  // Escuchar 403 global para mostrar mensaje al usuario
  useEffect(() => {
    let hideTimeout
    const handler = (e) => {
      const message = e.detail?.message || 'No tienes permisos para esta acción'
      if (hideTimeout) clearTimeout(hideTimeout)
      setForbiddenMessage(message)
      hideTimeout = setTimeout(() => setForbiddenMessage(null), 5000)
    }
    window.addEventListener('api:forbidden', handler)
    return () => {
      window.removeEventListener('api:forbidden', handler)
      if (hideTimeout) clearTimeout(hideTimeout)
    }
  }, [])

  // Aplicar clases globales según variante de layout.
  useEffect(() => {
    if (useAdminAppLayout) {
      document.body.classList.add('admin-app-ui')
      document.body.classList.remove('mobile-ui', 'desktop-ui', 'vet-owner-ui', 'vet-basic-ui')

      // La variante admin-app es oscura por diseño.
      if (!document.documentElement.classList.contains('dark')) {
        document.documentElement.classList.add('dark')
        forcedDarkRef.current = true
      }
    } else if (useVetOwnerLayout) {
      document.body.classList.add('admin-app-ui', 'vet-owner-ui')
      document.body.classList.remove('mobile-ui', 'desktop-ui', 'vet-basic-ui')
      if (!document.documentElement.classList.contains('dark')) {
        document.documentElement.classList.add('dark')
        forcedDarkRef.current = true
      }
    } else if (useVetBasicLayout) {
      document.body.classList.add('admin-app-ui', 'vet-basic-ui')
      document.body.classList.remove('mobile-ui', 'desktop-ui', 'vet-owner-ui')
      if (!document.documentElement.classList.contains('dark')) {
        document.documentElement.classList.add('dark')
        forcedDarkRef.current = true
      }
    } else if (useMobileLayout) {
      document.body.classList.add('mobile-ui')
      document.body.classList.remove('desktop-ui', 'admin-app-ui', 'vet-owner-ui', 'vet-basic-ui')
    } else {
      document.body.classList.add('desktop-ui')
      document.body.classList.remove('mobile-ui', 'admin-app-ui', 'vet-owner-ui', 'vet-basic-ui')
    }

    return () => {
      document.body.classList.remove('mobile-ui', 'desktop-ui', 'admin-app-ui', 'vet-owner-ui', 'vet-basic-ui')
      if (forcedDarkRef.current) {
        document.documentElement.classList.remove('dark')
        forcedDarkRef.current = false
      }
    }
  }, [useAdminAppLayout, useMobileLayout, useVetOwnerLayout, useVetBasicLayout])
  
  if (PAGES_WITHOUT_SIDEBAR.includes(location.pathname)) {
    return <Outlet />
  }
  
  if (useMobileLayout) {
    return (
      <>
        {forbiddenMessage && (
          <div
            role="alert"
            className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-gray-900 text-center py-2 px-4 text-sm font-medium shadow-md"
          >
            {forbiddenMessage}
          </div>
        )}
        <MobileLayout>
          <Outlet />
        </MobileLayout>
      </>
    )
  }

  if (useAdminAppLayout) {
    return (
      <>
        {forbiddenMessage && (
          <div
            role="alert"
            className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-gray-900 text-center py-2 px-4 text-sm font-medium shadow-md"
          >
            {forbiddenMessage}
          </div>
        )}
        <AdminAppLayout>
          <Outlet />
        </AdminAppLayout>
      </>
    )
  }

  if (useVetOwnerLayout) {
    return (
      <>
        {forbiddenMessage && (
          <div
            role="alert"
            className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-gray-900 text-center py-2 px-4 text-sm font-medium shadow-md"
          >
            {forbiddenMessage}
          </div>
        )}
        <VetOwnerLayout>
          <Outlet />
        </VetOwnerLayout>
      </>
    )
  }

  if (useVetBasicLayout) {
    return (
      <>
        {forbiddenMessage && (
          <div
            role="alert"
            className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-gray-900 text-center py-2 px-4 text-sm font-medium shadow-md"
          >
            {forbiddenMessage}
          </div>
        )}
        <VetBasicLayout>
          <Outlet />
        </VetBasicLayout>
      </>
    )
  }

  return (
    <>
      {forbiddenMessage && (
        <div
          role="alert"
          className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-gray-900 text-center py-2 px-4 text-sm font-medium shadow-md"
        >
          {forbiddenMessage}
        </div>
      )}
      <MainLayout>
        <Outlet />
      </MainLayout>
    </>
  )
}

